import React, { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { Checkbox, Icon } from "semantic-ui-react";

import { useDelay, useDropdownData } from "hooks";
import CompactButton from "components/CompactButton";
import Selector from "components/Selector";

import { HubContext } from "scenes/Hubly/components/Workspace/Provider";

import { NextWorkflowConditionsLauncher } from "..";

export function NextWorkflowAdd({
  disabled,
  enableConditions,
  workflow,
  onAdd,
  onClose,
}) {
  const { fetchWorkflows } = useContext(HubContext);
  const [conditionsData, setConditionsData] = useState({});
  const [searchText, setSearchText] = useState("");
  const { loading, data, hasMoreData, loadData, loadMoreData } =
    useDropdownData({
      type: "workflow",
      workflowId: workflow.id,
      fetchFunction: fetchWorkflows,
      searchText: searchText,
      first: 50,
    });

  const [nextWorkflow, setNextWorkflow] = useState(null);
  const [applyToAllClients, setApplyToAllClients] = useState(true);

  const delayCallback = useDelay(100);
  const performSearchText = (text) => {
    delayCallback(() => {
      setSearchText(text);
    });
  };

  const nextWorkflowOptions = useMemo(() => {
    return data.map(({ id, name }) => {
      return { text: name, key: id, value: id };
    });
  }, [data]);

  return (
    <React.Fragment>
      <div style={{ marginRight: "0.5em", marginTop: "1em" }}>
        <Selector
          name="nextWorkflowId"
          onOpen={loadData}
          showLoadMore={hasMoreData}
          loadMoreData={loadMoreData}
          dropdownProps={{
            disabled: disabled,
            loading: loading,
            selectOnBlur: false,
            placeholder: "Search for Workflow",
            className: "next-workflows-search-dropdown",
            noResultsMessage: "No Workflows to Add",
            onSearchChange: (_, { searchQuery }) => {
              performSearchText(searchQuery);
            },
          }}
          selection={nextWorkflowOptions}
          multiple={false}
          selected={nextWorkflow?.id || null}
          onSelect={(selectedId) => {
            performSearchText("");
            setNextWorkflow(
              data.find(({ id }) => {
                return id === selectedId;
              })
            );
          }}
        />
        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          data-test="next-workflow-add-options"
        >
          <Checkbox
            name="applyNextWorkflowsToAll"
            label="Apply to all clients"
            checked={applyToAllClients}
            onChange={() => {
              setApplyToAllClients(!applyToAllClients);
            }}
            style={{ marginTop: "0.5em" }}
          />
          {enableConditions && (
            <div style={{ marginTop: "0.4em", marginRight: "0.4em" }}>
              <NextWorkflowConditionsLauncher
                workflowId={workflow.id}
                onConfigChange={setConditionsData}
                item={{
                  ...conditionsData,
                  nextWorkflow: { name: nextWorkflow?.name },
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: "0.5em", marginBottom: "0.5em" }}>
        <CompactButton
          positive
          basic={false}
          minWidth={0}
          disabled={!nextWorkflow?.id || disabled}
          onClick={() => {
            onAdd({
              nextWorkflowId: nextWorkflow?.id,
              applyToAllClients: applyToAllClients,
              ...conditionsData,
            }).then((resp) => {
              if (resp) {
                setNextWorkflow(null);
                setConditionsData({});
              }
            });
          }}
        >
          Confirm
        </CompactButton>
        <Icon
          name="delete"
          color="grey"
          size="large"
          link
          disabled={disabled}
          onClick={() => {
            onClose();
          }}
        />
      </div>
    </React.Fragment>
  );
}

NextWorkflowAdd.propTypes = {
  disabled: PropTypes.bool,
  enableConditions: PropTypes.bool,
  workflow: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

NextWorkflowAdd.defaultProps = {
  disabled: false,
  enableConditions: false,
};

export default NextWorkflowAdd;
